import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader, EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, TrendingUp, TrendingDown, ChevronRight, ChevronDown, Receipt, Scale } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const VENDOR_COLORS = ["#0D5C3A", "#C85A32", "#D97706", "#84A98C", "#2563EB", "#9333EA"];
const EMPTY = { total: 0, count: 0, orders: [], by_counterpart: [], by_category: [] };

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pad = (n) => String(n).padStart(2, "0");
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const daysBetween = (a, b) => Math.round((parse(b) - parse(a)) / 86400000) + 1;
const fmtMonth = (m) => { const [y, mo] = m.split("-"); return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en", { month: "short" }) + " " + y.slice(2); };

const MODES = [
  { id: "month", label: "Month" },
  { id: "week", label: "Week" },
  { id: "day", label: "Day" },
  { id: "range", label: "Custom range" },
];

function computePeriods(mode, vals) {
  if (mode === "month") {
    const [y, m] = vals.month.split("-").map(Number);
    const cs = new Date(y, m - 1, 1), ce = new Date(y, m, 0);
    const ps = new Date(y, m - 2, 1), pe = new Date(y, m - 1, 0);
    return { cur: { start: fmt(cs), end: fmt(ce) }, prev: { start: fmt(ps), end: fmt(pe) }, prevLabel: "previous month" };
  }
  if (mode === "day") {
    const d = parse(vals.day);
    const p = addDays(d, -1);
    return { cur: { start: fmt(d), end: fmt(d) }, prev: { start: fmt(p), end: fmt(p) }, prevLabel: "previous day" };
  }
  if (mode === "week") {
    const d = parse(vals.week);
    const mon = addDays(d, -((d.getDay() + 6) % 7));
    const sun = addDays(mon, 6);
    return { cur: { start: fmt(mon), end: fmt(sun) }, prev: { start: fmt(addDays(mon, -7)), end: fmt(addDays(sun, -7)) }, prevLabel: "previous week" };
  }
  const L = daysBetween(vals.start, vals.end);
  const ps = parse(vals.start);
  const prevEnd = addDays(ps, -1);
  const prevStart = addDays(prevEnd, -(L - 1));
  return { cur: { start: vals.start, end: vals.end }, prev: { start: fmt(prevStart), end: fmt(prevEnd) }, prevLabel: "previous period" };
}

export default function Reports() {
  const today = new Date();
  const [mode, setMode] = useState("month");
  const [view, setView] = useState("earnings"); // earnings (as seller) | expenses (as buyer)
  const [vals, setVals] = useState({
    month: `${today.getFullYear()}-${pad(today.getMonth() + 1)}`,
    day: fmt(today),
    week: fmt(today),
    start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)),
    end: fmt(today),
  });
  const [loading, setLoading] = useState(true);
  const [cur, setCur] = useState({ earnings: EMPTY, expenses: EMPTY });
  const [prev, setPrev] = useState({ earnings: EMPTY, expenses: EMPTY });
  const [prevLabel, setPrevLabel] = useState("previous month");
  const [trend, setTrend] = useState([]);

  const load = useCallback(async () => {
    if (mode === "range") {
      if (vals.start > vals.end) { toast.error("Start date must be before end date"); return; }
      if (daysBetween(vals.start, vals.end) > 31) { toast.error("Range cannot exceed one month (31 days)"); return; }
    }
    setLoading(true);
    const p = computePeriods(mode, vals);
    setPrevLabel(p.prevLabel);
    try {
      const [c, pr] = await Promise.all([
        api.get("/reports/summary", { params: p.cur }),
        api.get("/reports/summary", { params: p.prev }),
      ]);
      setCur(c.data); setPrev(pr.data);
    } catch (e) { toast.error("Failed to load report"); } finally { setLoading(false); }
  }, [mode, vals]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/reports/trend").then((r) => setTrend(r.data.trend)).catch(() => {}); }, []);

  const earn = cur.earnings || EMPTY;
  const exp = cur.expenses || EMPTY;
  const net = earn.total - exp.total;
  const active = view === "earnings" ? earn : exp;
  const prevActive = (view === "earnings" ? prev.earnings : prev.expenses) || EMPTY;
  const diff = active.total - prevActive.total;
  const pct = prevActive.total === 0 ? (active.total > 0 ? 100 : 0) : (diff / prevActive.total) * 100;
  const up = diff >= 0;
  const accent = view === "earnings" ? "#0D5C3A" : "#C85A32";
  const noData = earn.count === 0 && exp.count === 0;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-stone-900 sm:text-3xl">Reports</h1>
        <p className="mt-1 text-stone-500">Your Earnings as a seller and Expenses as a buyer, side by side. Includes Delivered &amp; Missed Delivery (billed on received quantities).</p>
      </div>

      {/* period controls */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-[#E5E0D8] bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {MODES.map((m) => (
            <button key={m.id} data-testid={`report-mode-${m.id}`} onClick={() => setMode(m.id)}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${mode === m.id ? "border-[#0D5C3A] bg-[#0D5C3A] text-white" : "border-[#E5E0D8] bg-white text-stone-600 hover:border-[#84A98C]"}`}>
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {mode === "month" && (
            <div><Label className="text-xs">Month</Label><Input data-testid="report-month-input" type="month" className="mt-1" value={vals.month} onChange={(e) => setVals((v) => ({ ...v, month: e.target.value }))} /></div>
          )}
          {mode === "day" && (
            <div><Label className="text-xs">Day</Label><Input data-testid="report-day-input" type="date" className="mt-1" value={vals.day} onChange={(e) => setVals((v) => ({ ...v, day: e.target.value }))} /></div>
          )}
          {mode === "week" && (
            <div><Label className="text-xs">Any day in week</Label><Input data-testid="report-week-input" type="date" className="mt-1" value={vals.week} onChange={(e) => setVals((v) => ({ ...v, week: e.target.value }))} /></div>
          )}
          {mode === "range" && (
            <>
              <div><Label className="text-xs">From</Label><Input data-testid="report-range-start" type="date" className="mt-1" value={vals.start} onChange={(e) => setVals((v) => ({ ...v, start: e.target.value }))} /></div>
              <div><Label className="text-xs">To</Label><Input data-testid="report-range-end" type="date" className="mt-1" value={vals.end} onChange={(e) => setVals((v) => ({ ...v, end: e.target.value }))} /></div>
            </>
          )}
        </div>
      </div>

      {loading ? <Loader /> : (
        <>
          {/* summary cards: earnings, expenses, net */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm" data-testid="report-earnings-card">
              <div className="flex items-center gap-2 text-stone-400"><TrendingUp className="h-4 w-4 text-[#0D5C3A]" /><span className="text-xs font-semibold uppercase tracking-wider">Earnings (as seller)</span></div>
              <p className="mt-2 font-heading text-3xl font-extrabold text-[#0D5C3A]" data-testid="report-earnings-total">{money(earn.total)}</p>
              <p className="mt-1 text-xs text-stone-500">{earn.count} order(s)</p>
            </div>
            <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm" data-testid="report-expenses-card">
              <div className="flex items-center gap-2 text-stone-400"><TrendingDown className="h-4 w-4 text-[#C85A32]" /><span className="text-xs font-semibold uppercase tracking-wider">Expenses (as buyer)</span></div>
              <p className="mt-2 font-heading text-3xl font-extrabold text-[#C85A32]" data-testid="report-expenses-total">{money(exp.total)}</p>
              <p className="mt-1 text-xs text-stone-500">{exp.count} order(s)</p>
            </div>
            <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm" data-testid="report-net-card">
              <div className="flex items-center gap-2 text-stone-400"><Scale className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">Net (earnings − expenses)</span></div>
              <p className={`mt-2 font-heading text-3xl font-extrabold ${net >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="report-net-total">{net < 0 ? "−" : ""}{money(Math.abs(net))}</p>
              <p className="mt-1 text-xs text-stone-500">across the selected period</p>
            </div>
          </div>

          {/* trend line: earnings vs expenses */}
          {trend.length > 0 && (
            <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm" data-testid="report-trend">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-stone-400">Earnings vs Expenses — last 6 months</p>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trend} margin={{ top: 8, right: 20, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0ECE1" />
                  <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 12, fill: "#57534e" }} tickLine={false} axisLine={false} />
                  <YAxis tickFormatter={(v) => `₹${v}`} tick={{ fontSize: 12, fill: "#57534e" }} width={64} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => money(v)} labelFormatter={fmtMonth} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line name="Earnings" type="monotone" dataKey="earnings" stroke="#0D5C3A" strokeWidth={2.5} dot={{ r: 3, fill: "#0D5C3A" }} activeDot={{ r: 5 }} />
                  <Line name="Expenses" type="monotone" dataKey="expenses" stroke="#C85A32" strokeWidth={2.5} dot={{ r: 3, fill: "#C85A32" }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* view toggle for the detailed breakdown */}
          <div className="flex items-center gap-2" data-testid="report-view-toggle">
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">Breakdown:</span>
            <button data-testid="report-view-earnings" onClick={() => setView("earnings")}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${view === "earnings" ? "border-[#0D5C3A] bg-[#0D5C3A] text-white" : "border-[#E5E0D8] bg-white text-stone-600 hover:border-[#84A98C]"}`}>Earnings (selling)</button>
            <button data-testid="report-view-expenses" onClick={() => setView("expenses")}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${view === "expenses" ? "border-[#C85A32] bg-[#C85A32] text-white" : "border-[#E5E0D8] bg-white text-stone-600 hover:border-[#84A98C]"}`}>Expenses (buying)</button>
          </div>

          {/* vs-previous for active view */}
          <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm" data-testid="report-change-card">
            <div className="flex items-center gap-2 text-stone-400">{up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}<span className="text-xs font-semibold uppercase tracking-wider">{view === "earnings" ? "Earnings" : "Expenses"} vs {prevLabel}</span></div>
            <p className={`mt-2 font-heading text-2xl font-extrabold ${up ? "text-emerald-600" : "text-rose-600"}`} data-testid="report-change">{up ? "+" : "−"}{money(Math.abs(diff))}</p>
            <p className={`text-sm font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`} data-testid="report-change-pct">{up ? "+" : "−"}{Math.abs(pct).toFixed(1)}% · was {money(prevActive.total)}</p>
          </div>

          {/* insights charts */}
          {active.orders.length > 0 && (
            <div className="grid gap-4 lg:grid-cols-2" data-testid="report-charts">
              <MiniBars title={view === "earnings" ? "Earnings by Buyer" : "Expenses by Supplier"} data={active.by_counterpart || []} color={accent} testid="report-chart-vendor" />
              <MiniBars title={view === "earnings" ? "Earnings by Category" : "Expenses by Category"} data={active.by_category || []} color="#D97706" testid="report-chart-category" />
            </div>
          )}

          {/* supplier price trends (buying view) */}
          {view === "expenses" && <VendorPriceTrends />}

          {/* table */}
          {noData ? (
            <EmptyState icon={Receipt} title="No orders in this period" description="Delivered and Missed Delivery orders will appear here." testid="report-empty" />
          ) : active.orders.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E5E0D8] bg-white p-8 text-center text-sm text-stone-400" data-testid="report-view-empty">
              No {view === "earnings" ? "earnings (selling)" : "expenses (buying)"} orders in this period.
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-[#E5E0D8] bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF8F5] text-left text-xs uppercase tracking-wider text-stone-400">
                  <tr>
                    <th className="px-5 py-3 w-8"></th>
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">{view === "earnings" ? "Buyer" : "Supplier"}</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3 text-right">{view === "earnings" ? "Earned" : "Spent"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0ECE1]">
                  {active.orders.map((o) => <ReportRow key={o.id} o={o} />)}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MiniBars({ title, data, color, testid }) {
  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm" data-testid={testid}>
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-stone-400">{title}</p>
      {data.length === 0 ? (
        <p className="py-8 text-center text-sm text-stone-400">No data in this period</p>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(130, data.length * 46)}>
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12, fill: "#57534e" }} tickLine={false} axisLine={false} />
            <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#FAF8F5" }} />
            <Bar dataKey="total" radius={[0, 6, 6, 0]} fill={color} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function VendorPriceTrends() {
  const [products, setProducts] = useState([]);
  const [sel, setSel] = useState("");
  useEffect(() => {
    api.get("/reports/price-trends").then((r) => {
      setProducts(r.data.products);
      if (r.data.products.length) setSel(r.data.products[0].key);
    }).catch(() => {});
  }, []);
  if (products.length === 0) return null;
  const prod = products.find((p) => p.key === sel) || products[0];
  const vendors = [...new Set(prod.points.map((p) => p.vendor))];
  const byDate = {};
  prod.points.forEach((pt) => {
    byDate[pt.date] = byDate[pt.date] || { date: pt.date };
    byDate[pt.date][pt.vendor] = pt.unit_price;
  });
  const data = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm" data-testid="report-price-trends">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">Supplier price trends</p>
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger className="h-9 w-56 bg-white" data-testid="price-trend-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
          <SelectContent>
            {products.map((p) => <SelectItem key={p.key} value={p.key}>{p.product_name} ({p.unit})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 8, right: 20, bottom: 4, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F0ECE1" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#57534e" }} tickLine={false} axisLine={false} />
          <YAxis tickFormatter={(v) => `₹${v}`} tick={{ fontSize: 11, fill: "#57534e" }} width={56} tickLine={false} axisLine={false} />
          <Tooltip formatter={(v) => money(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {vendors.map((v, i) => (
            <Line key={v} type="monotone" dataKey={v} stroke={VENDOR_COLORS[i % VENDOR_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <p className="mt-2 text-xs text-stone-400">Unit price per {prod.unit} over time — spot increases before they hit your bill.</p>
    </div>
  );
}

function ReportRow({ o }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="cursor-pointer hover:bg-[#FAF8F5]" onClick={() => setOpen((x) => !x)} data-testid={`report-row-${o.order_code}`}>
        <td className="px-5 py-3 text-stone-400">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
        <td className="px-5 py-3 text-stone-600">{o.date}</td>
        <td className="px-5 py-3"><span className="text-stone-800">{o.counterpart}</span> <span className="block font-mono text-xs text-stone-400">{o.counterpart_code}</span></td>
        <td className="px-5 py-3"><StatusBadge status={o.status} /></td>
        <td className="px-5 py-3 text-right font-semibold text-stone-800" data-testid={`report-amount-${o.order_code}`}>{money(o.amount)}</td>
      </tr>
      {open && (
        <tr data-testid={`report-detail-${o.order_code}`}>
          <td></td>
          <td colSpan={4} className="px-5 pb-4">
            <div className="overflow-hidden rounded-xl border border-[#E5E0D8]">
              <table className="w-full text-xs">
                <thead className="bg-[#FAF8F5] text-left uppercase text-stone-400">
                  <tr><th className="px-3 py-2">Product</th><th className="px-3 py-2">Qty</th><th className="px-3 py-2">Unit</th><th className="px-3 py-2 text-right">Unit Price</th><th className="px-3 py-2 text-right">Line Total</th></tr>
                </thead>
                <tbody className="divide-y divide-[#F0ECE1]">
                  {o.items.map((it, i) => (
                    <tr key={`${it.product_name}-${it.unit}-${i}`}>
                      <td className="px-3 py-2 text-stone-700">{it.product_name}</td>
                      <td className="px-3 py-2">{it.quantity}</td>
                      <td className="px-3 py-2 text-stone-500">{it.unit}</td>
                      <td className="px-3 py-2 text-right text-stone-600">{it.unit_price != null ? money(it.unit_price) : "—"}</td>
                      <td className="px-3 py-2 text-right font-medium text-stone-800">{it.line_total != null ? money(it.line_total) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
